import { Dropzone as MantineDropzone } from "@mantine/dropzone";
import { IconAlertCircle, IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import React, { useMemo, useState } from "react";
import { logger } from "~/utils/logger";
import { browserSafeUuid } from "~/utils/types/uuid";
import { ActionIcon } from "../controls/buttons";
import { CloseButton } from "../primitives/buttons";
import { Flex, type FlexProps, Group } from "../primitives/layout";
import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";
import { Text } from "../primitives/typography";
import { tokens } from "../tokens";

import styles from "./dropzone.module.css";
import { LoadingOverlay } from "./feedback";

/**
 * Owned copies of Mantine 6.0.19's MIME groups. They are public `~/ui` values used by the
 * uploader family and SDS dropzones, so they cannot re-export the vendor declaration surface.
 */
export const IMAGE_MIME_TYPE = ["image/png", "image/gif", "image/jpeg", "image/svg+xml", "image/webp", "image/avif"];
export const PDF_MIME_TYPE = ["application/pdf"];
export const MS_WORD_MIME_TYPE = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
export const MS_EXCEL_MIME_TYPE = ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
export const MS_POWERPOINT_MIME_TYPE = [
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

export type DropzoneAccept = Record<string, string[]> | string[];

/**
 * The file payloads the dropzone hands its callbacks, owned at the shapes Mantine 6's react-dropzone
 * 14.2.3 declared (FileWithPath from its file-selector). Mantine 7 replaced that package with its
 * react-dropzone-esm fork, so react-dropzone is no longer installed and the call sites' shapes are
 * restated here (SC-2703); dropzone-casts pins that Mantine 7's payloads satisfy them. The one
 * difference from Mantine 6's declaration is that `errors` is readonly, as the fork declares it:
 * a callback cannot be handed a readonly array as a mutable one, and no consumer writes to it.
 */
export interface FileWithPath extends File {
  readonly path?: string;
}

export interface FileError {
  message: string;
  code: string;
}

export interface FileRejection {
  file: File;
  errors: readonly FileError[];
}

export interface DropzoneProps extends Omit<ElementProps<"div">, "onDrop">, StyleProps {
  children: React.ReactNode;
  variant?: string;
  /** Mantine 7 dropped Dropzone's `padding`; the wrapper sets the `p` style prop from it, which takes the same values. */
  padding?: SizeValue;
  radius?: SizeValue;
  disabled?: boolean;
  onDrop(files: FileWithPath[]): void;
  onReject?(fileRejections: FileRejection[]): void;
  onDropAny?(files: FileWithPath[], fileRejections: FileRejection[]): void;
  loading?: boolean;
  accept?: DropzoneAccept;
  openRef?: React.ForwardedRef<() => void | undefined>;
  multiple?: boolean;
  maxSize?: number;
  name?: string;
  maxFiles?: number;
  autoFocus?: boolean;
  activateOnClick?: boolean;
  activateOnDrag?: boolean;
  activateOnKeyboard?: boolean;
  dragEventsBubbling?: boolean;
  onFileDialogCancel?(): void;
  onFileDialogOpen?(): void;
  preventDropOnDocument?: boolean;
  useFsAccessApi?: boolean;
}

export interface DropzoneStatusProps {
  children: React.ReactNode;
}

/**
 * Mantine carries the underlying react-dropzone types through its public prop map. The private
 * cast keeps that vendor surface inside src/ui; dropzone-casts pins every owned forwarded key, and
 * that Mantine 7's react-dropzone-esm payloads satisfy the owned payload types.
 */
const DropzoneRoot = MantineDropzone as React.ComponentType<Omit<DropzoneProps, "padding"> & { readonly ref?: React.Ref<HTMLDivElement> }>;
const DropzoneAcceptStatus = MantineDropzone.Accept as React.ComponentType<DropzoneStatusProps>;
const DropzoneRejectStatus = MantineDropzone.Reject as React.ComponentType<DropzoneStatusProps>;
const DropzoneIdleStatus = MantineDropzone.Idle as React.ComponentType<DropzoneStatusProps>;

/**
 * Mantine 6 read `padding` as a theme spacing key or number (default `md`); Mantine 7 has no such
 * prop and pads the root with `md` from its stylesheet, so the owned value becomes the `p` style prop,
 * which resolves the same keys and numbers (SC-2703). An explicit `p` wins over it.
 */
const DropzoneBase = React.forwardRef<HTMLDivElement, DropzoneProps>(function Dropzone({ padding, p, ...props }, ref) {
  return <DropzoneRoot {...props} p={p ?? padding} ref={ref} />;
});

export const Dropzone = Object.assign(DropzoneBase, {
  Accept: DropzoneAcceptStatus,
  Reject: DropzoneRejectStatus,
  Idle: DropzoneIdleStatus,
});

const DEFAULT_MAX_FILE_UPLOAD_SIZE_MB = 15;
const DEFAULT_MAX_UPLOAD_SIZE = DEFAULT_MAX_FILE_UPLOAD_SIZE_MB * 1048 ** 2;
const REJECTED_FILES_MAX_HEIGHT = 80;
const REJECTED_FILES_MARGIN_TOP = 12;
const UPLOAD_ZONE_PADDING = 4;

const UPLOAD_ZONE_LINE_HEIGHT = (height: number) => height - 2 * UPLOAD_ZONE_PADDING;
const DROPZONE_MAX_HEIGHT = (height: number) => REJECTED_FILES_MAX_HEIGHT + REJECTED_FILES_MARGIN_TOP + height;

const ACCEPTED_FILE_TYPES = [IMAGE_MIME_TYPE, PDF_MIME_TYPE, MS_WORD_MIME_TYPE, MS_EXCEL_MIME_TYPE, MS_POWERPOINT_MIME_TYPE].flatMap(
  type => type,
);

export type RejectedFile<M extends string | null | undefined = undefined> = M extends undefined
  ? {
      readonly file: FileRejection;
      readonly onClose: () => void;
    }
  : RejectedFile & { readonly message: M };

export interface RejectedFilesProps extends Omit<FlexProps, "direction" | "align"> {
  readonly files: RejectedFile[];
}

const REJECTED_FILE_ERROR_MESSAGES: Record<string, string> = {
  "file-invalid-type": "The file type is not supported.",
  "file-too-large": "The file is too large.",
  "file-too-small": "The file is too small.",
  "too-many-files": "Too many files were uploaded.",
};

const getRejectedErrorMessage = (rejection: FileRejection): string | null => {
  if (rejection.errors.length !== 0) {
    logger.info(`File was rejected with the following errors:\n${JSON.stringify(rejection.errors)}`);
    const error = rejection.errors[0];
    const message = error ? REJECTED_FILE_ERROR_MESSAGES[error.code] : undefined;
    if (message !== undefined) {
      return message;
    }
    return "There was an error uploading the file.";
  }
  logger.error("Dropzone unexpectedly rejected a file and did not provide any error messages.");
  return null;
};

export const RejectedFiles = ({ files, ...props }: RejectedFilesProps): React.JSX.Element => {
  const rejectedErrors = useMemo<RejectedFile<string>[]>(
    () =>
      files
        .map((file): RejectedFile<string | null> => {
          const message = getRejectedErrorMessage(file.file);
          return { ...file, message };
        })
        .filter((file: RejectedFile<string | null>): file is RejectedFile<string> => file.message !== null),
    [files],
  );

  if (rejectedErrors.length === 0) {
    return <></>;
  }

  return (
    <Flex {...props} direction="column" align="center" gap={4}>
      {rejectedErrors.map((rejection, index) => (
        <Flex key={index} direction="column" w="100%">
          <Flex direction="row" align="center" w="100%" p="xs" style={{ borderRadius: 2 }}>
            <Flex key={index} direction="row" align="center" style={{ flexGrow: 100 }}>
              <IconAlertCircle size="1rem" color={tokens.color.content.danger} />
              <Text size="xs" ml="xs">
                {rejection.message}
              </Text>
            </Flex>
            <ActionIcon size="1rem" onClick={() => rejection.onClose()}>
              <IconX size="1rem" color={tokens.color.content.secondary} />
            </ActionIcon>
          </Flex>
          <Text size="xxs" ml={24} color="dimmed">
            {rejection.file.file.name}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

type RejectedFileWithId = RejectedFile & { readonly id: string };

const rejectedFilesWithControls = (
  files: FileRejection[],
  setRejectedFiles: React.Dispatch<React.SetStateAction<RejectedFileWithId[]>>,
): RejectedFileWithId[] =>
  files.map(file => {
    const id = browserSafeUuid();
    return {
      file,
      id,
      onClose: () => setRejectedFiles(current => current.filter(rejection => rejection.id !== id)),
    };
  });

/** File selector that reports the first accepted file to its owner. */
export interface FileDropzoneProps extends Omit<FlexProps, "direction" | "mah" | "onChange"> {
  onChange: (payload: File | null) => void;
  file?: File | null;
  readonly dropzoneHeight?: number;
  readonly multiple?: boolean;
  readonly maxUploadSize?: number;
}

export const FileDropzone = ({
  multiple = false,
  file,
  onChange,
  dropzoneHeight = 32,
  maxUploadSize = DEFAULT_MAX_UPLOAD_SIZE,
  ...flexProps
}: FileDropzoneProps): React.JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedFileWithId[]>([]);

  const onDrop = async (files: FileWithPath[]) => {
    setLoading(true);
    onChange(files?.[0] || null);
    setLoading(false);
  };

  const onReject = (files: FileRejection[]) => {
    setRejectedFiles(current => [...current, ...rejectedFilesWithControls(files, setRejectedFiles)]);
  };

  return (
    <Flex {...flexProps} pos="relative" direction="column" mah={DROPZONE_MAX_HEIGHT(dropzoneHeight)}>
      <Flex direction="row" style={{ alignItems: "center" }}>
        <Dropzone
          multiple={multiple}
          color="gray.4"
          style={{
            padding: 4,
            height: 64,
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            flexBasis: "100%",
          }}
          onDrop={onDrop}
          onReject={onReject}
          maxSize={maxUploadSize}
          accept={ACCEPTED_FILE_TYPES}
          disabled={loading}
        >
          <Group position="center" spacing="xl" style={{ pointerEvents: "none", position: "relative" }}>
            <LoadingOverlay visible={loading} loaderProps={{ size: "xs", color: "gray.6" }} overlayOpacity={0} />
            <Flex align="center">
              <Dropzone.Accept>
                <IconUpload size={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} stroke={1.5} color={tokens.color.content.muted} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} stroke={1.5} color={tokens.color.content.muted} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} stroke={1.5} color={tokens.color.content.muted} />
              </Dropzone.Idle>
              <Text size="xs" color="dimmed" ml={10} lh={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)}>
                {file?.name ?? "Drag files here or click to select files"}
              </Text>
            </Flex>
          </Group>
        </Dropzone>
        {file?.name != null && <CloseButton onClick={() => onChange(null)} variant="transparent" pos="absolute" right="0px" w="2.25rem" />}
      </Flex>
      <RejectedFiles files={rejectedFiles} mt={REJECTED_FILES_MARGIN_TOP} mah={REJECTED_FILES_MAX_HEIGHT} style={{ overflowY: "scroll" }} />
    </Flex>
  );
};

/** File uploader that deduplicates a drop and waits for every upload before clearing loading. */
export interface UploadDropzoneProps extends Omit<FlexProps, "direction" | "mah"> {
  readonly upload: (file: FileWithPath) => Promise<void>;
  readonly dropzoneHeight?: number;
  readonly multiple?: boolean;
  readonly maxUploadSize?: number;
}

export const UploadDropzone = ({
  multiple = true,
  upload,
  dropzoneHeight = 32,
  maxUploadSize = DEFAULT_MAX_UPLOAD_SIZE,
  ...flexProps
}: UploadDropzoneProps): React.JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedFileWithId[]>([]);

  const onDrop = async (files: FileWithPath[]) => {
    const seen = new Set<string>();
    const unique = files.filter(file => {
      const key = `${file.name}|${file.size}|${file.lastModified}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    setLoading(true);
    try {
      await Promise.all(unique.map(file => upload(file)));
    } finally {
      setLoading(false);
    }
  };

  const onReject = (files: FileRejection[]) => {
    setRejectedFiles(current => [...current, ...rejectedFilesWithControls(files, setRejectedFiles)]);
  };

  return (
    <Flex {...flexProps} direction="column" mah={DROPZONE_MAX_HEIGHT(dropzoneHeight)}>
      <Dropzone
        multiple={multiple}
        color="gray.4"
        style={{ padding: 4, height: 64, display: "flex", flexDirection: "column", justifyContent: "center" }}
        onDrop={onDrop}
        onReject={onReject}
        maxSize={maxUploadSize}
        accept={ACCEPTED_FILE_TYPES}
        disabled={loading}
      >
        <Group position="center" spacing="xl" style={{ pointerEvents: "none", position: "relative" }}>
          <LoadingOverlay visible={loading} loaderProps={{ size: "xs", color: "gray.6" }} overlayOpacity={0} />
          <Flex align="center" className={styles.dropzoneIcon}>
            <Dropzone.Accept>
              <IconUpload size={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} stroke={1.5} color={tokens.color.content.muted} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} stroke={1.5} color={tokens.color.content.muted} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} stroke={1.5} color={tokens.color.content.muted} />
            </Dropzone.Idle>
            <Text size="xs" color="dimmed" ml={10} lh={UPLOAD_ZONE_LINE_HEIGHT(dropzoneHeight)} className={styles.dropzoneHint}>
              Drag files here or click to select files
            </Text>
          </Flex>
        </Group>
      </Dropzone>
      <RejectedFiles files={rejectedFiles} mt={REJECTED_FILES_MARGIN_TOP} mah={REJECTED_FILES_MAX_HEIGHT} style={{ overflowY: "scroll" }} />
    </Flex>
  );
};
